import { useId } from 'react'

import {
  Badge,
  Card,
  Flex,
  GridItem,
  HStack,
  Stack,
  Text,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { WarningMessage } from 'src/components/WarningMessage/WarningMessage'
import { useCurrentMember, usePermission } from 'src/features/auth/context'
import {
  ExplanationReservationCard_TrialPatientFragment,
  ExplanationReservationInviteeType,
  ExplanationRevisionStatusV2,
  ExplanationType,
} from 'src/lib/gql-client'
import { PERMISSIONS } from 'src/lib/permission'
import { assertNever } from 'src/utils/assertNever'
import { formatDate } from 'src/utils/formatDate'

import { isAgreementCompleted } from '../../utils/isAgreementCompleted'
import { CancelReservationButton } from '../CancelReservationButton/CancelReservationButton'
import { CreateReservationButton } from '../CreateReservationButton/CreateReservationButton'
import { UpdateReservationButton } from '../UpdateReservationButton/UpdateReservationButton'

dayjs.extend(isSameOrAfter)

type Props = {
  trialPatient: ExplanationReservationCard_TrialPatientFragment
  explanationRevisionUid: string
  explanationType: ExplanationType
  revisionStatus: ExplanationRevisionStatusV2
  mainHospitalName: string
  partnerHospitalName?: string
  isPartnerMemberNotAssigned?: boolean
  mutateExplanation: () => void
}

// 説明に次回の予約の可能性があるかを返す
const getCanHaveNextReservation = (
  status: ExplanationRevisionStatusV2,
  explanationType: ExplanationType,
): boolean => {
  if (explanationType === 'InPerson') {
    switch (status) {
      case 'AgreementCompleted':
      case 'DeliveryCompleted':
        return true
      default:
        return false
    }
  }
  switch (status) {
    case 'New':
    case 'SessionStarted':
    case 'SignCompleted':
    case 'SignReady':
    case 'SignStarted':
    case 'AgreementCompleted':
    case 'DeliveryCompleted':
      return true
    case 'Disabled':
    case 'Withdrawn':
    case 'AgreementRejected':
    case 'Invalid':
      return false
    default:
      return false
  }
}

export const ExplanationReservationCardV2: React.FC<Props> = ({
  trialPatient,
  revisionStatus,
  explanationRevisionUid,
  explanationType,
  partnerHospitalName,
  isPartnerMemberNotAssigned,
  mutateExplanation,
}) => {
  const titleId = useId()

  const { selectedTrialHospitalRole } = useCurrentMember()

  const { hasPermission } = usePermission()

  const canEditExplanation = hasPermission(PERMISSIONS.Explanation_Edit)

  // 説明に次回の予約の可能性があるか
  const canHaveNextReservation = getCanHaveNextReservation(
    revisionStatus,
    explanationType,
  )

  // 予約する権限があるかどうか
  const canReserve =
    canEditExplanation &&
    selectedTrialHospitalRole === 'Main' &&
    canHaveNextReservation

  const { reservations: availableReservations } = trialPatient

  const getInviteeStr = (
    inviteeType: ExplanationReservationInviteeType,
    partnerHospitalName: string | undefined,
  ) => {
    switch (inviteeType) {
      case 'PartnerMembers':
        return selectedTrialHospitalRole === 'Partner'
          ? undefined // パートナー施設の場合は予約の案内先は表示しない
          : partnerHospitalName
      case 'Patient':
        return '患者'
      default:
        return assertNever(inviteeType)
    }
  }

  const pastReservations = availableReservations.filter(
    reservation => reservation.timeStatus === 'Past',
  )
  const upcomingReservations = availableReservations.filter(
    reservation => reservation.timeStatus === 'Upcoming',
  )

  // 古い予約は新しい順に表示
  pastReservations.sort(
    (a, b) =>
      new Date(b.reservedAt).getTime() - new Date(a.reservedAt).getTime(),
  )

  return (
    <Card as="section" aria-labelledby={titleId} p="5">
      <Stack>
        <Flex justify="space-between">
          <Text as="h2" fontWeight="bold" id={titleId}>
            面談予約一覧
          </Text>
          {canReserve && (
            <CreateReservationButton
              explanationRevisionUid={explanationRevisionUid}
              explanationType={explanationType}
              isPartnerMemberNotAssigned={isPartnerMemberNotAssigned}
              partnerHospitalName={partnerHospitalName}
              mutateExplanation={mutateExplanation}
              isAgreementCompleted={isAgreementCompleted(revisionStatus)}
            />
          )}
        </Flex>

        <GridItem>
          {canHaveNextReservation && upcomingReservations?.length == 0 && (
            <WarningMessage
              message={
                selectedTrialHospitalRole === 'Partner'
                  ? '次回面談予定が設定されていません。実施医療機関の案内をお待ちください。'
                  : '次回面談予定が設定されていません。'
              }
            />
          )}
          {canHaveNextReservation &&
            upcomingReservations.map((nextReservation, index) => {
              if (!nextReservation.explanationReservation) {
                return null
              }
              const inviteeStr = nextReservation.explanationReservation
                ? getInviteeStr(
                    nextReservation.explanationReservation
                      .reservationInviteeType,
                    nextReservation.explanationReservation.partnerTrialHospital
                      ?.hospital.name,
                  )
                : undefined
              return (
                // /* 説明方式=パートナー で、患者との予約の場合はパートナー医療機関に予約日時の案内は表示しない  */
                <Flex
                  key={index}
                  justify="space-between"
                  alignItems="center"
                  padding={4}
                  borderLeftWidth={index === 0 ? '2px' : undefined}
                  borderLeftColor={index === 0 ? 'blue.400' : undefined}
                  borderBottom={
                    pastReservations.length !== 0 ||
                    upcomingReservations.length !== 1
                      ? '1px'
                      : undefined
                  }
                  borderBottomColor="gray.200"
                >
                  <Stack gap={0}>
                    <HStack gap={2}>
                      <Text fontWeight="bold">
                        {formatDate(
                          nextReservation.reservedAt,
                          'YYYY/MM/DD (ddd) HH:mm',
                        )}
                      </Text>
                      {index === 0 && (
                        <Badge
                          bg="white"
                          color="blue.400"
                          px="2"
                          py="1"
                          borderRadius="sm"
                          borderColor="blue.400"
                          borderWidth="1px"
                        >
                          <span>次回面談</span>
                        </Badge>
                      )}
                    </HStack>
                    {inviteeStr && (
                      <Text
                        as="span"
                        flex="1"
                        overflow="hidden"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        fontSize="sm"
                        textColor="gray.600"
                      >
                        案内先：{inviteeStr}
                      </Text>
                    )}
                  </Stack>
                  {
                    <Stack>
                      {canReserve && (
                        <HStack gap={1}>
                          <UpdateReservationButton
                            explanationType={explanationType}
                            explanationReservationUid={
                              nextReservation.explanationReservation
                                .explanationReservationUid
                            }
                            inviteeType={
                              nextReservation.explanationReservation
                                .reservationInviteeType
                            }
                            reservedAt={nextReservation.reservedAt}
                            isPartnerMemberNotAssigned={
                              isPartnerMemberNotAssigned
                            }
                            partnerHospitalName={partnerHospitalName}
                            mutateExplanation={mutateExplanation}
                            isAgreementCompleted={isAgreementCompleted(
                              revisionStatus,
                            )}
                          />
                          <CancelReservationButton
                            explanationReservationUid={
                              nextReservation.explanationReservation
                                .explanationReservationUid
                            }
                            mutateExplanation={mutateExplanation}
                          />
                        </HStack>
                      )}
                    </Stack>
                  }
                </Flex>
              )
            })}
        </GridItem>
        <GridItem colSpan={2}>
          {
            // 予約がある場合は表示
            pastReservations.map((reservation, index) => {
              if (!reservation.explanationReservation) {
                return null
              }
              const inviteeStr = getInviteeStr(
                reservation.explanationReservation.reservationInviteeType,
                reservation.explanationReservation.partnerTrialHospital
                  ?.hospital.name,
              )
              return (
                <HStack
                  paddingLeft={4}
                  spacing="4"
                  wrap="wrap"
                  rowGap="1"
                  key={index}
                  textColor="gray.500"
                  fontSize="sm"
                >
                  <Text>
                    {formatDate(
                      reservation.reservedAt,
                      'YYYY/MM/DD (ddd) HH:mm',
                    )}
                  </Text>
                  {inviteeStr && (
                    <Text>
                      案内先：
                      {inviteeStr}
                    </Text>
                  )}
                </HStack>
              )
            })
          }
        </GridItem>
      </Stack>
    </Card>
  )
}
